import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRightLeft, ArrowDownLeft, ArrowUpRight, Plus, Search, Warehouse, Package, Tag, X, CheckCircle2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function MovementsPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [form, setForm] = useState({
    product_name: '',
    movement_type: 'INBOUND',
    quantity: '1',
    warehouse_name: 'انبار مرکزی تهران',
    notes: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/inventory/movements', { search }],
    queryFn: () => api.get('/inventory/movements', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newMov: typeof form) => api.post('/inventory/movements', {
      ...newMov,
      quantity: Number(newMov.quantity) || 1
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/inventory/movements'] })
      toast.success('تردد جدید کالا با موفقیت ثبت شد')
      setIsOpenModal(false)
      setForm({ product_name: '', movement_type: 'INBOUND', quantity: '1', warehouse_name: 'انبار مرکزی تهران', notes: '' })
    },
    onError: () => toast.error('خطا در ثبت حرکت انبار')
  })

  const items = data?.items || [
    {
      id: 'mov_1',
      movement_number: 'MOV-2026-001',
      product_name: 'گوشی موبایل سامسونگ Galaxy A55',
      movement_type: 'INBOUND',
      quantity: 10,
      warehouse_name: 'انبار مرکزی تهران',
      created_at: '2026-08-01T10:00:00Z',
      notes: 'ورود از خرید فاکتور PO-102'
    },
    {
      id: 'mov_2',
      movement_number: 'MOV-2026-002',
      product_name: 'لپ‌تاپ ایسوس Vivobook 15',
      movement_type: 'OUTBOUND',
      quantity: 2,
      warehouse_name: 'انبار مرکزی تهران',
      created_at: '2026-08-01T14:30:00Z',
      notes: 'خروج بابت فاکتور INV-9081'
    },
    {
      id: 'mov_3',
      movement_number: 'MOV-2026-003',
      product_name: 'هندزفری بادی فنری هایلو',
      movement_type: 'TRANSFER',
      quantity: 5,
      warehouse_name: 'انبار خاوران',
      created_at: '2026-08-01T16:00:00Z',
      notes: 'انتقال بین انبارها'
    }
  ]

  const filteredItems = items.filter((item: any) => {
    if (typeFilter !== 'ALL' && item.movement_type !== typeFilter) return false
    if (search && !item.product_name?.includes(search) && !item.notes?.includes(search)) return false
    return true
  })

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <ArrowRightLeft className="w-6 h-6 text-primary-500" />
            گردش و حرکات انبار
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            سوابق ورود، خروج، انتقال و تعدیل موجودی کالاها در انبارها
          </p>
        </div>
        <Button onClick={() => setIsOpenModal(true)}>
          <Plus className="w-4 h-4 ml-1" />
          ثبت گردش جدید
        </Button>
      </motion.div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4 border-r-4 border-r-emerald-500">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <ArrowDownLeft className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">ورود کالا (Inbound)</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white mt-0.5">
              {items.filter((i: any) => i.movement_type === 'INBOUND').length} مورد
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4 border-r-4 border-r-red-500">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
            <ArrowUpRight className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">خروج کالا (Outbound)</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white mt-0.5">
              {items.filter((i: any) => i.movement_type === 'OUTBOUND').length} مورد
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4 border-r-4 border-r-blue-500">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <ArrowRightLeft className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">انتقال بین انبارها</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white mt-0.5">
              {items.filter((i: any) => i.movement_type === 'TRANSFER').length} مورد
            </p>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="جستجوی نام کالا یا توضیحات..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">همه نوع گردش‌ها</option>
            <option value="INBOUND">ورود (خرید / مرجوعی)</option>
            <option value="OUTBOUND">خروج (فروش / ضایعات)</option>
            <option value="TRANSFER">انتقال انبار</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-6 w-48" /></div>)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="card p-12 text-center">
          <ArrowRightLeft className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">گردشی ثبت نشده است</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">کالا</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">نوع گردش</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">تعداد</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">انبار</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">توضیحات</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">تاریخ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {filteredItems.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Package className="w-4 h-4 text-primary-500" />
                      {item.product_name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.movement_type === 'INBOUND' ? (
                        <Badge variant="success" className="gap-1">
                          <ArrowDownLeft className="w-3.5 h-3.5" />
                          ورود
                        </Badge>
                      ) : item.movement_type === 'OUTBOUND' ? (
                        <Badge variant="danger" className="gap-1">
                          <ArrowUpRight className="w-3.5 h-3.5" />
                          خروج
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="gap-1">
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                          انتقال
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-neutral-900 dark:text-white">
                      {item.quantity} عدد
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                      <div className="flex items-center gap-1.5">
                        <Warehouse className="w-3.5 h-3.5 text-neutral-400" />
                        {item.warehouse_name || 'انبار اصلی'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{item.notes || '---'}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('fa-IR') : 'امروز'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Add Movement */}
      <AnimatePresence>
        {isOpenModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-primary-500" />
                  ثبت گردش کالا در انبار
                </h3>
                <button onClick={() => setIsOpenModal(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.product_name) {
                    toast.error('نام کالا الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام کالا</label>
                  <Input
                    placeholder="مثلا: گوشی سامسونگ A55"
                    value={form.product_name}
                    onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نوع گردش</label>
                  <select
                    value={form.movement_type}
                    onChange={(e) => setForm({ ...form, movement_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="INBOUND">ورود به انبار (خرید / دریافت)</option>
                    <option value="OUTBOUND">خروج از انبار (فروش / ارسال)</option>
                    <option value="TRANSFER">انتقال به انبار دیگر</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">تعداد / مقدار</label>
                  <Input
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام انبار</label>
                  <Input
                    placeholder="انبار مرکزی"
                    value={form.warehouse_name}
                    onChange={(e) => setForm({ ...form, warehouse_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">توضیحات یا بابت</label>
                  <Input
                    placeholder="علت ثبت گردش..."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpenModal(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ثبت حرکت
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
